import React, { createContext, useContext, useEffect, useState } from 'react';
import { fetchPublicSettings } from '../services/api';

interface WebsiteLockContextValue {
  isLocked: boolean;
  message: string;
  loading: boolean;
  error: string | null;
  refresh: () => Promise<void>;
}

const WebsiteLockContext = createContext<WebsiteLockContextValue>({
  isLocked: false,
  message: 'We are currently not available for booking, please check back later.',
  loading: true,
  error: null,
  refresh: async () => {}
});

export const WebsiteLockProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [isLocked, setIsLocked] = useState(false);
  const [message, setMessage] = useState('We are currently not available for booking, please check back later.');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const refresh = async () => {
    setLoading(true);
    setError(null);

    try {
      const settings = await fetchPublicSettings();
      setIsLocked(Boolean(settings.maintenance_mode));
      setMessage(settings.maintenance_message || 'We are currently not available for booking, please check back later.');
    } catch (err: any) {
      setError(err?.message || 'Unable to load site lock status');
      setIsLocked(false);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refresh();
  }, []);

  return (
    <WebsiteLockContext.Provider value={{ isLocked, message, loading, error, refresh }}>
      {children}
    </WebsiteLockContext.Provider>
  );
};

export const useWebsiteLock = () => useContext(WebsiteLockContext);
